import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Award, Users, CheckCircle, Calendar, Star, Crown, Shield, Package, Play } from 'lucide-react';
import { fetchProgram, fetchReferentielUrls, createCheckout, getUserPurchasedPrograms } from '../services/api';
import { PROGRAM_REFERENTIALS } from '../mock/referentials';
import { toast } from 'sonner';
import CoursePreviewModal from '../components/CoursePreviewModal';
import PreCheckoutModal from '../components/PreCheckoutModal';

const ProgramDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [REFERENTIEL_URLS, setReferentielUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [pendingFormula, setPendingFormula] = useState(null);
  const pricingRef = useRef(null);
  const [hasPurchased, setHasPurchased] = useState(false);

  // Check if user has purchased this program
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.email) {
      getUserPurchasedPrograms(user.email)
        .then(transactions => {
          const purchased = transactions.some(t => t.programId === id);
          setHasPurchased(purchased);
        })
        .catch(() => {});
    }
  }, [id]);

  useEffect(() => {
    Promise.all([
      fetchProgram(id).catch(() => null),
      fetchReferentielUrls().catch(() => ({}))
    ]).then(([prog, urls]) => {
      setProgram(prog);
      setReferentielUrls(urls);
      setLoading(false);
    });
  }, [id]);

  const referential = PROGRAM_REFERENTIALS[id];

  const handleCheckout = async (formula) => {
    setPendingFormula(formula);
    setCheckoutModalOpen(true);
  };

  const handleCheckoutConfirm = async ({ nom, prenom, email, telephone }) => {
    setCheckoutModalOpen(false);
    setCheckoutLoading(pendingFormula);
    try {
      const result = await createCheckout({
        programId: id,
        formula: pendingFormula,
        email,
        nom,
        prenom,
        telephone,
        origin: window.location.origin
      });
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (err) {
      toast.error('Erreur lors du paiement. Réessayez.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!program || !referential) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h1 className="heading-1" style={{ marginBottom: '1rem' }}>Formation non trouvée</h1>
          <Link to="/programs" className="btn-primary">Retour aux formations</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
      {/* Hero Section */}
      <section style={{ 
        background: 'var(--gradient-hero-warm)',
        padding: '4rem 1.2rem 3rem'
      }}>
        <div className="container" style={{ maxWidth: '1000px' }}>
          <button
            onClick={() => navigate('/programs')}
            className="btn-secondary"
            style={{ marginBottom: '2rem', gap: '0.5rem' }}
          >
            <ArrowLeft size={18} />
            <span>Retour aux formations</span>
          </button>

          <div style={{
            display: 'inline-block',
            padding: '0.25rem 0.75rem',
            background: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '1rem',
            marginBottom: '1rem'
          }}>
            <span className="caption font-mono" style={{ textTransform: 'uppercase' }}>
              {program.title}
            </span>
          </div>

          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>
            {program.fullName}
          </h1>

          <p className="body-large" style={{ marginBottom: '2rem', maxWidth: '800px' }}>
            {program.description}
          </p>

          <div style={{ 
            display: 'flex',
            gap: '2rem',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Award size={20} />
              <span className="body-medium">Accès plateforme 1 an</span>
            </div>
          </div>
        </div>
      </section>

      <div className="container" style={{ padding: '3rem 1.2rem' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          {/* Découvrir le diplôme CTA */}
          {(() => {
            const INTRO_PRESENTATIONS = {
              'deap': { presentationType: 'deap', title: 'Introduction au DEAP' },
              'deeje': { presentationType: 'deeje', title: 'Introduction au DEEJE' },
              'bac-assp': { presentationType: 'bac-assp', title: 'Introduction au BAC ASSP' },
              'cap-aepe': { presentationType: 'cap-aepe', title: 'Introduction au CAP AEPE' },
              'tp': { presentationType: 'tp-iepe', title: 'Introduction au TP IEPE' },
              'caferuis': { presentationType: 'caferuis', title: 'Introduction au CAFERUIS' },
              'deas': { presentationType: 'deas', title: 'Introduction au DEAS' },
            };
            const intro = INTRO_PRESENTATIONS[id];
            if (!intro) return null;
            return (
              <section style={{ marginBottom: '3rem' }}>
                <div data-testid="intro-presentation-cta" onClick={() => setIntroOpen(true)} style={{
                  background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
                  borderRadius: '1rem',
                  padding: '2rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '1.5rem',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                  border: '1px solid rgba(255,255,255,0.05)'
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                    <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid rgba(59,130,246,0.2)' }}>
                      <Play size={26} color="#60A5FA" />
                    </div>
                    <div>
                      <h3 style={{ color: 'white', fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                        Découvrir le diplôme
                      </h3>
                      <p style={{ color: '#94A3B8', fontSize: '0.9rem', lineHeight: 1.4 }}>
                        Présentation interactive gratuite — parcours, épreuves et débouchés
                      </p>
                    </div>
                  </div>
                  <div style={{ background: '#3B82F6', padding: '0.6rem 1.25rem', borderRadius: '0.5rem', color: 'white', fontSize: '0.85rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                    Voir la présentation
                  </div>
                </div>
              </section>
            );
          })()}

          {/* Description */}
          <section style={{ marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
              Présentation du diplôme
            </h2>
            <div className="card" style={{ padding: '1.5rem' }}>
              <p className="body-medium" style={{ lineHeight: '1.8' }}>
                {referential.description}
              </p>
            </div>
          </section>

          {/* Objectifs */}
          <section style={{ marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
              Objectifs de la formation
            </h2>
            <div className="card accent-purple" style={{ padding: '1.5rem' }}>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {referential.objectives.map((objective, index) => (
                  <li key={index} style={{ 
                    display: 'flex',
                    gap: '1rem',
                    marginBottom: '1rem',
                    alignItems: 'flex-start'
                  }}>
                    <CheckCircle size={20} style={{ flexShrink: 0, marginTop: '0.2rem', color: 'green' }} />
                    <span className="body-medium">{objective}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </div>

        {/* ===== COURSE PREVIEW SECTION ===== */}
        {/* Purchased user banner */}
        {hasPurchased && (
          <section style={{ marginBottom: '2rem' }}>
            <div data-testid="purchased-banner" style={{
              background: 'linear-gradient(135deg, #059669, #10B981)',
              color: 'white', padding: '1rem 1.5rem', borderRadius: '12px',
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
              maxWidth: '1100px', margin: '0 auto'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <CheckCircle size={22} />
                <div>
                  <strong style={{ fontSize: '0.95rem' }}>Vous avez accès à cette formation !</strong>
                  <p style={{ fontSize: '0.8rem', opacity: 0.9, margin: 0 }}>Accédez à tous les cours sans limite depuis votre espace candidat.</p>
                </div>
              </div>
              <Link
                to={`/dashboard?tab=courses`}
                data-testid="go-to-dashboard-btn"
                style={{
                  background: 'white', color: '#059669', padding: '0.6rem 1.25rem',
                  borderRadius: '8px', fontWeight: 700, fontSize: '0.85rem',
                  textDecoration: 'none', whiteSpace: 'nowrap'
                }}
              >
                Aller au dashboard
              </Link>
            </div>
          </section>
        )}

        {/* ===== PRICING SECTION ===== */}
        <section ref={pricingRef} style={{ marginBottom: '3rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '0.5rem' }}>Nos formules</h2>
            <p className="body-medium" style={{ color: 'var(--text-muted)' }}>Accès valable 1 an à compter de la date d'achat</p>
          </div>

          {/* CPF notice */}
          <div style={{
            maxWidth: '800px', margin: '0 auto 2rem',
            background: 'var(--bg-section)', borderRadius: '12px',
            padding: '1rem 1.5rem', textAlign: 'center', lineHeight: 1.7
          }}>
            <p className="body-small">
              Les formules présentées ci-dessous sont accessibles sur <strong>financement personnel</strong>, avec paiement possible en plusieurs fois.
            </p>
            <p className="body-small" style={{ marginTop: '0.5rem' }}>
              Ce ne sont pas des formules disponibles via le CPF. Pour découvrir les formules CPF, <a href="/cpf" style={{ color: '#E75480', fontWeight: 600, textDecoration: 'underline' }}>cliquez ici</a>.
            </p>
          </div>

          <div className="grid-responsive-3" style={{
            gap: '1.5rem',
            maxWidth: '1100px',
            margin: '0 auto'
          }}>
            {/* BRONZE */}
            <div style={{
              borderRadius: '16px', overflow: 'hidden',
              border: '2px solid #d4a574',
              background: 'linear-gradient(180deg, #f5e6d3 0%, #fff 15%)',
              display: 'flex', flexDirection: 'column'
            }} data-testid="tier-bronze">
              <div style={{
                background: 'linear-gradient(135deg, #d4a574, #c4956a)',
                padding: '1rem 1.5rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem'
              }}>
                <Shield size={22} style={{ color: 'white' }} />
                <span style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.05em' }}>BRONZE</span>
              </div>
              <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '2.25rem', fontWeight: 700, color: '#8B6914' }}>{program.priceBronze}€</span>
                </div>
                <p className="caption" style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Accès 1 an · Paiement unique ou en 3x</p>
                <p className="body-small" style={{ fontWeight: 600, marginBottom: '1rem' }}>Cette formule comprend :</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
                  {(program.featuresBronze || []).map((f, i) => (
                    <li key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', alignItems: 'flex-start' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '0.15rem', color: '#c4956a' }} />
                      <span className="body-small">{f}</span>
                    </li>
                  ))}
                </ul>
                {program.taglineBronze && (
                  <p style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e8d5c4', fontSize: '0.8rem', color: '#8B6914', fontStyle: 'italic' }}>
                    {program.taglineBronze}
                  </p>
                )}
                <button
                  onClick={() => handleCheckout('bronze')}
                  disabled={checkoutLoading === 'bronze'}
                  data-testid="buy-bronze"
                  style={{
                    marginTop: '1.25rem', width: '100%', padding: '0.85rem', borderRadius: '10px',
                    border: '2px solid #c4956a', background: 'white', color: '#8B6914',
                    fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s'
                  }}
                >
                  {checkoutLoading === 'bronze' ? 'Redirection...' : 'Choisir cette formule'}
                </button>
                <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                  ou 3x {Math.round(program.priceBronze / 3)}€ sans frais
                </p>
              </div>
            </div>

            {/* SILVER */}
            <div style={{
              borderRadius: '16px', overflow: 'hidden',
              border: '2px solid #9b59b6',
              background: 'linear-gradient(180deg, #f0e6f6 0%, #fff 15%)',
              display: 'flex', flexDirection: 'column',
              transform: 'scale(1.03)',
              boxShadow: '0 8px 32px rgba(155, 89, 182, 0.2)'
            }} data-testid="tier-silver">
              <div style={{ textAlign: 'center', padding: '0.35rem', background: '#8e44ad' }}>
                <span style={{ color: 'white', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Recommandé</span>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #9b59b6, #8e44ad)',
                padding: '1rem 1.5rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem'
              }}>
                <Star size={22} style={{ color: 'white' }} />
                <span style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.05em' }}>ARGENT</span>
              </div>
              <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '2.25rem', fontWeight: 700, color: '#8e44ad' }}>{program.priceSilver}€</span>
                </div>
                <p className="caption" style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Accès 1 an · Paiement unique ou en 3x</p>
                <p className="body-small" style={{ fontWeight: 600, marginBottom: '1rem' }}>Cette formule comprend :</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
                  {(program.featuresSilver || []).map((f, i) => (
                    <li key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', alignItems: 'flex-start' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '0.15rem', color: '#9b59b6' }} />
                      <span className="body-small">{f}</span>
                    </li>
                  ))}
                </ul>
                {program.taglineSilver && (
                  <p style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #d8bfe8', fontSize: '0.8rem', color: '#8e44ad', fontStyle: 'italic' }}>
                    {program.taglineSilver}
                  </p>
                )}
                <button
                  onClick={() => handleCheckout('silver')}
                  disabled={checkoutLoading === 'silver'}
                  data-testid="buy-silver"
                  style={{
                    marginTop: '1.25rem', width: '100%', padding: '0.85rem', borderRadius: '10px',
                    border: 'none', background: 'linear-gradient(135deg, #9b59b6, #8e44ad)', color: 'white',
                    fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s',
                    boxShadow: '0 4px 12px rgba(155, 89, 182, 0.3)'
                  }}
                >
                  {checkoutLoading === 'silver' ? 'Redirection...' : 'Choisir cette formule'}
                </button>
                <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                  ou 3x {Math.round(program.priceSilver / 3)}€ sans frais
                </p>
              </div>
            </div>

            {/* GOLD */}
            <div style={{
              borderRadius: '16px', overflow: 'hidden',
              border: '2px solid #d4af37',
              background: 'linear-gradient(180deg, #fdf6e3 0%, #fff 15%)',
              display: 'flex', flexDirection: 'column'
            }} data-testid="tier-gold">
              <div style={{
                background: 'linear-gradient(135deg, #d4af37, #b8960c)',
                padding: '1rem 1.5rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem'
              }}>
                <Crown size={22} style={{ color: 'white' }} />
                <span style={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.05em' }}>OR</span>
              </div>
              <div style={{ padding: '1.5rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '2.25rem', fontWeight: 700, color: '#b8960c' }}>{program.priceGold}€</span>
                </div>
                <p className="caption" style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Accès 1 an · Paiement unique ou en 3x</p>
                <p className="body-small" style={{ fontWeight: 600, marginBottom: '1rem' }}>Cette formule comprend :</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
                  {(program.featuresGold || []).map((f, i) => (
                    <li key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem', alignItems: 'flex-start' }}>
                      <CheckCircle size={16} style={{ flexShrink: 0, marginTop: '0.15rem', color: '#d4af37' }} />
                      <span className="body-small">{f}</span>
                    </li>
                  ))}
                </ul>
                {program.taglineGold && (
                  <p style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e8dbb0', fontSize: '0.8rem', color: '#b8960c', fontStyle: 'italic' }}>
                    {program.taglineGold}
                  </p>
                )}
                <button
                  onClick={() => handleCheckout('gold')}
                  disabled={checkoutLoading === 'gold'}
                  data-testid="buy-gold"
                  style={{
                    marginTop: '1.25rem', width: '100%', padding: '0.85rem', borderRadius: '10px',
                    border: '2px solid #d4af37', background: 'white', color: '#b8960c',
                    fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s'
                  }}
                >
                  {checkoutLoading === 'gold' ? 'Redirection...' : 'Choisir cette formule'}
                </button>
                <p style={{ textAlign: 'center', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>
                  ou 3x {Math.round(program.priceGold / 3)}€ sans frais
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Kits par Bloc section */}
        {program.hasKitPricing && program.kits && program.kits.length > 0 && (
          <section style={{ marginBottom: '3rem', maxWidth: '1100px', margin: '0 auto 3rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.75rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '0.75rem 1.5rem', borderRadius: '2rem',
                marginBottom: '1rem'
              }}>
                <Package size={20} color="white" />
                <span style={{ color: 'white', fontWeight: 700, fontSize: '0.9rem' }}>KITS PAR BLOC</span>
              </div>
              <h2 className="heading-2" style={{ marginBottom: '0.5rem' }}>
                Achetez bloc par bloc
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-muted)' }}>
                Vous souhaitez rédiger en autonomie ? Achetez uniquement le bloc qui vous pose problème.
              </p>
              <p className="body-small" style={{ color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                Chaque kit comprend le contenu indiqué. Certains kits incluent un RDV de 15 minutes et/ou la possibilité d'ajouter un appel d'explication en option.
              </p>
              <p className="body-small" style={{ color: '#92400E', marginTop: '0.75rem', background: '#FEF3C7', padding: '0.6rem 1rem', borderRadius: '0.5rem', border: '1px solid #F59E0B40' }}>
                Conformément aux conditions d'accès aux contenus numériques, les formations, constituées exclusivement d'outils pédagogiques au format PDF, ne sont ni échangeables ni remboursables une fois l'accès activé.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.25rem'
            }}>
              {program.kits.map((kit) => (
                <div key={kit.id} className="card hover-lift" style={{
                  padding: '1.25rem', textAlign: 'center',
                  border: `2px solid ${kit.color}20`,
                  transition: 'all 0.2s'
                }} data-testid={`kit-card-${kit.id}`}>
                  <div style={{
                    background: kit.color, borderRadius: '0.75rem',
                    width: '44px', height: '44px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 0.75rem', color: 'white', fontWeight: 700, fontSize: '1rem'
                  }}>
                    {kit.id.match(/\d+/)?.[0] || kit.id.split('-').pop()}
                  </div>
                  <h4 style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.25rem' }}>{kit.title}</h4>
                  <p className="body-small" style={{ color: 'var(--text-muted)', marginBottom: '0.5rem', fontSize: '0.75rem' }}>{kit.subtitle}</p>
                  <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
                    {(kit.activities || kit.includes || kit.blocs || []).join(' · ')}
                  </p>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: kit.color, marginBottom: '0.75rem' }}>{kit.price}€</div>
                  <button
                    className="btn-primary"
                    style={{ width: '100%', fontSize: '0.75rem', padding: '0.5rem' }}
                    onClick={async () => {
                      setCheckoutLoading(kit.id);
                      try {
                        const data = await createCheckout({
                          programId: id,
                          formula: 'kit',
                          kitId: kit.id,
                          kitTitle: kit.title,
                          price: kit.price,
                          origin: window.location.origin,
                          email: ''
                        });
                        if (data.url) window.location.href = data.url;
                      } catch (e) {
                        toast.error("Erreur lors du paiement");
                      }
                      setCheckoutLoading(null);
                    }}
                    disabled={checkoutLoading === kit.id}
                    data-testid={`buy-kit-${kit.id}`}
                  >
                    {checkoutLoading === kit.id ? 'Redirection...' : 'Acheter ce kit'}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Référentiel de compétences */}
          <section style={{ marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
              Référentiel de compétences
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {referential.competences.map((bloc, index) => (
                <div key={index} className={`card accent-${['blue', 'orange', 'green', 'pink', 'grey'][index % 5]}`}>
                  <h3 className="heading-2" style={{ marginBottom: '1rem' }}>
                    {bloc.bloc}
                  </h3>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {bloc.competences.map((competence, cIndex) => (
                      <li key={cIndex} style={{ 
                        display: 'flex',
                        gap: '0.75rem',
                        marginBottom: '0.75rem',
                        alignItems: 'flex-start',
                        paddingLeft: '1rem',
                        position: 'relative'
                      }}>
                        <span style={{
                          position: 'absolute',
                          left: 0,
                          top: '0.4rem',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: 'var(--text-primary)'
                        }} />
                        <span className="body-small">{competence}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* Débouchés */}
          <section style={{ marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>
              Débouchés professionnels
            </h2>
            <div className="card accent-green" style={{ padding: '1.5rem' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                {referential.debouches.map((debouche, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.5)',
                    borderRadius: '0.5rem'
                  }}>
                    <Award size={18} style={{ flexShrink: 0 }} />
                    <span className="body-small">{debouche}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* CTA Section */}
      <section style={{ 
        padding: '4rem 1.2rem',
        background: 'var(--bg-section)',
        textAlign: 'center'
      }}>
        <div className="container" style={{ maxWidth: '700px' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
            Des questions sur cette formation ?
          </h2>
          <p className="body-large" style={{ marginBottom: '2rem' }}>
            Nos conseillers sont à votre disposition pour vous accompagner dans votre projet VAE
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="mailto:apdr.vae@gmail.com" className="btn-primary">
              Nous contacter
            </a>
            <Link to="/booking" className="btn-secondary">
              Réserver un entretien gratuit
            </Link>
          </div>
        </div>
      </section>

      <PreCheckoutModal
        open={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
        onConfirm={handleCheckoutConfirm}
        loading={!!checkoutLoading}
      />

      {/* Intro Presentation Modal (no lock - free presentation) */}
      <CoursePreviewModal
        isOpen={introOpen}
        onClose={() => setIntroOpen(false)}
        presentationType={
          { 'deap': 'deap', 'deeje': 'deeje', 'bac-assp': 'bac-assp', 'cap-aepe': 'cap-aepe', 'tp': 'tp-iepe', 'caferuis': 'caferuis', 'deas': 'deas' }[id] || id
        }
        courseTitle={`Introduction au ${program?.title || id.toUpperCase()}`}
        noLock={true}
        onScrollToPricing={() => {
          if (pricingRef.current) {
            pricingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
      />
    </div>
  );
};

export default ProgramDetailPage;
